/**
 * Como a tela mostra a data de cancelamento da UC (solicitação 6005).
 *
 * A data é carimbada pelo banco (gatilho trg_uc_carimba_cancelamento) quando a
 * situação vira "Cancelada". Para as UCs que já estavam canceladas antes disso, a
 * migration 20260928150000 recuperou uma data ESTIMADA do histórico — e a tela
 * precisa deixar isso claro, senão "em 12/05/2026" vindo de uma planilha importada
 * parece tão certo quanto um carimbo feito na hora.
 */

export type OrigemCancelamentoUc = 'registrada' | 'estimada_rateio' | 'estimada_conclusao' | 'estimada_distrato';

const FONTE_ESTIMATIVA: Record<Exclude<OrigemCancelamentoUc, 'registrada'>, string> = {
    estimada_rateio: 'data de retirada do rateio da solicitação de cancelamento',
    estimada_conclusao: 'data de conclusão da solicitação de cancelamento',
    estimada_distrato: 'data do distrato',
};

export interface RotuloCancelamentoUc {
    /** "em 12/05/2026" ou "data não registrada". */
    texto: string;
    /** A data veio do histórico, não de um carimbo feito na hora. */
    estimada: boolean;
    /** Explicação para o `title`. */
    explicacao: string;
}

const fmt = (iso: string) => {
    // A data do cancelamento é do dia em Brasília: 23h de um dia em UTC já é o
    // dia seguinte em Goiás e mostraria a data errada.
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

export function estaCancelada(situacao: string | null | undefined): boolean {
    return String(situacao ?? '').trim() === 'Cancelada';
}

/** null quando a UC não está cancelada — aí não há o que mostrar. */
export function rotuloCancelamentoUc(uc: {
    situacao: string | null | undefined;
    cancelada_em?: string | null;
    cancelada_em_origem?: string | null;
}): RotuloCancelamentoUc | null {
    if (!estaCancelada(uc.situacao)) return null;
    const data = uc.cancelada_em ? fmt(uc.cancelada_em) : null;
    if (!data) {
        return {
            texto: 'data não registrada',
            estimada: false,
            explicacao: 'A UC foi cancelada antes de o sistema registrar a data, e o histórico não tem solicitação concluída nem distrato de onde recuperá-la.',
        };
    }
    const origem = uc.cancelada_em_origem as OrigemCancelamentoUc | null | undefined;
    if (origem && origem !== 'registrada' && FONTE_ESTIMATIVA[origem]) {
        return {
            texto: `em ${data}`,
            estimada: true,
            explicacao: `Data estimada: a UC já estava cancelada quando o sistema passou a registrar a data, então foi usada a ${FONTE_ESTIMATIVA[origem]}.`,
        };
    }
    return { texto: `em ${data}`, estimada: false, explicacao: `Cancelada em ${data}, registrado pelo sistema no momento da mudança.` };
}
